import {Alert, Badge, Button, Group, List, ScrollArea, Stack, Tabs, Text, Title} from '@mantine/core';
import {AiOutlineCheckCircle, AiOutlineCloseCircle, AiOutlineExclamationCircle, AiOutlineImport} from 'react-icons/ai';
import {useImportAllCodePipelinesMutation} from '../hooks/useCodePipelinesQuery';
import {ImportAllCodePipelinesResult} from '../types';
import {PlatformDrawer} from '@flexinet/ui-components';

type Result = ImportAllCodePipelinesResult['importAllCodePipelines'];

interface ImportAllResultDrawerProps {
    opened: boolean;
    onClose: () => void;
}

export const ImportAllResultDrawer = ({opened, onClose}: ImportAllResultDrawerProps) => {

    const importAllMutation = useImportAllCodePipelinesMutation();

    const result = importAllMutation.data as Result | undefined;
    const imported = result?.imported ?? [];
    const skipped = result?.skipped ?? [];
    const errors = result?.errors ?? [];

    const handleRunImport = () => {
        importAllMutation.mutate();
    };

    const handleClose = () => {
        importAllMutation.reset();
        onClose();
    };

    return (
        <PlatformDrawer
            opened={opened}
            onClose={handleClose}
            size='lg'
            title={<Title order={4}>Import all code pipelines</Title>}
        >
            <Stack>
                <Alert color='blue' icon={<AiOutlineExclamationCircle/>}>
                    Runs a bulk import of every AWS CodePipeline into the managed pipeline registry.
                    Pipelines that already exist will be skipped.
                </Alert>

                {!result && (
                    <Group>
                        <Button
                            leftSection={<AiOutlineImport/>}
                            onClick={handleRunImport}
                            loading={importAllMutation.isPending}
                        >
                            Run Import
                        </Button>
                        <Button variant='subtle' onClick={handleClose}
                                disabled={importAllMutation.isPending}>Cancel</Button>
                    </Group>
                )}

                {result && (
                    <>
                        <Group gap='xs'>
                            <Badge color='green' leftSection={<AiOutlineCheckCircle/>}>
                                {imported.length} imported
                            </Badge>
                            <Badge color='gray'>
                                {skipped.length} skipped
                            </Badge>
                            <Badge color='red' leftSection={<AiOutlineCloseCircle/>}>
                                {errors.length} errors
                            </Badge>
                        </Group>

                        <Tabs defaultValue='imported'>
                            <Tabs.List>
                                <Tabs.Tab value='imported'>Imported ({imported.length})</Tabs.Tab>
                                <Tabs.Tab value='skipped'>Skipped ({skipped.length})</Tabs.Tab>
                                <Tabs.Tab value='errors'>Errors ({errors.length})</Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value='imported' pt='sm'>
                                <ScrollArea h={320}>
                                    {imported.length === 0 ? (
                                        <Text c='dimmed' size='sm'>No pipelines imported.</Text>
                                    ) : (
                                        <List spacing='xs' size='sm'>
                                            {imported.map((p) => (
                                                <List.Item key={p.id}>
                                                    <Text fw={500}>{p.name ?? p.id}</Text>
                                                    <Text size='xs' c='dimmed'>
                                                        {p.pipelineType ?? '—'} · {p.pipelineStatus ?? '—'}
                                                    </Text>
                                                </List.Item>
                                            ))}
                                        </List>
                                    )}
                                </ScrollArea>
                            </Tabs.Panel>

                            <Tabs.Panel value='skipped' pt='sm'>
                                <ScrollArea h={320}>
                                    {skipped.length === 0 ? (
                                        <Text c='dimmed' size='sm'>No pipelines skipped.</Text>
                                    ) : (
                                        <List spacing='xs' size='sm'>
                                            {skipped.map((name, i) => (
                                                <List.Item key={`${name}-${i}`}>
                                                    <Text size='sm'>{name}</Text>
                                                </List.Item>
                                            ))}
                                        </List>
                                    )}
                                </ScrollArea>
                            </Tabs.Panel>

                            <Tabs.Panel value='errors' pt='sm'>
                                <ScrollArea h={320}>
                                    {errors.length === 0 ? (
                                        <Text c='dimmed' size='sm'>No errors.</Text>
                                    ) : (
                                        <List spacing='xs' size='sm'>
                                            {errors.map((e, i) => (
                                                <List.Item key={i}>
                                                    <Text size='sm' c='red'>{e}</Text>
                                                </List.Item>
                                            ))}
                                        </List>
                                    )}
                                </ScrollArea>
                            </Tabs.Panel>
                        </Tabs>

                        <Group justify='flex-end'>
                            <Button variant='subtle' onClick={handleClose}>Close</Button>
                        </Group>
                    </>
                )}
            </Stack>
        </PlatformDrawer>
    );
};
